/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import { supabase } from './supabase'

const CLIP_THUMBNAILS_BUCKET = 'clip-thumbnails'
const VFX_SHOT_THUMBNAILS_BUCKET = 'vfx-shot-thumbnails'

export interface UploadThumbnailResult {
  path: string
  url: string
}

export async function uploadClipThumbnail(
  projectId: string,
  clipId: string,
  file: File
): Promise<UploadThumbnailResult> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  const fileExt = file.name.split('.').pop() || 'jpg'
  const fileName = `thumbnail.${fileExt}`
  const filePath = `${projectId}/clips/${clipId}/${fileName}`

  const { error: uploadError } = await supabase.storage
    .from(CLIP_THUMBNAILS_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) {
    console.error('Error uploading clip thumbnail:', uploadError)
    throw uploadError
  }

  // Get signed URL for private bucket (valid for 1 year)
  const { data, error: urlError } = await supabase.storage
    .from(CLIP_THUMBNAILS_BUCKET)
    .createSignedUrl(filePath, 31536000) // 1 year in seconds

  if (urlError || !data) {
    console.error('Error getting signed URL:', urlError)
    throw urlError || new Error('Failed to get signed URL')
  }

  return {
    path: filePath,
    url: data.signedUrl,
  }
}

export async function deleteClipThumbnail(thumbnailPath: string): Promise<void> {
  const { error } = await supabase.storage
    .from(CLIP_THUMBNAILS_BUCKET)
    .remove([thumbnailPath])

  if (error) {
    console.error('Error deleting clip thumbnail:', error)
    throw error
  }
}

export async function uploadVfxShotThumbnail(
  projectId: string,
  vfxShotId: string,
  file: File
): Promise<UploadThumbnailResult> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    throw new Error('User not authenticated')
  }

  const fileExt = file.name.split('.').pop() || 'jpg'
  const fileName = `thumbnail.${fileExt}`
  const filePath = `${projectId}/vfx-shots/${vfxShotId}/${fileName}`

  const { error: uploadError } = await supabase.storage
    .from(VFX_SHOT_THUMBNAILS_BUCKET)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true,
    })

  if (uploadError) {
    console.error('Error uploading VFX shot thumbnail:', uploadError)
    throw uploadError
  }

  // Get signed URL for private bucket (valid for 1 year)
  const { data, error: urlError } = await supabase.storage
    .from(VFX_SHOT_THUMBNAILS_BUCKET)
    .createSignedUrl(filePath, 31536000) // 1 year in seconds

  if (urlError || !data) {
    console.error('Error getting signed URL:', urlError)
    throw urlError || new Error('Failed to get signed URL')
  }

  return {
    path: filePath,
    url: data.signedUrl,
  }
}

export async function deleteVfxShotThumbnail(thumbnailPath: string): Promise<void> {
  const { error } = await supabase.storage
    .from(VFX_SHOT_THUMBNAILS_BUCKET)
    .remove([thumbnailPath])

  if (error) {
    console.error('Error deleting VFX shot thumbnail:', error)
    throw error
  }
}

// Cache signed URLs to avoid regenerating them
const urlCache = new Map<string, { url: string; expiresAt: number }>()

export async function getClipThumbnailUrl(thumbnailPath: string | null | undefined): Promise<string | null> {
  if (!thumbnailPath) return null

  // Check cache first
  const cacheKey = `clip:${thumbnailPath}`
  const cached = urlCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url
  }

  // Generate new signed URL (valid for 7 days)
  const expiresInSeconds = 7 * 24 * 60 * 60 // 7 days
  const { data, error } = await supabase.storage
    .from(CLIP_THUMBNAILS_BUCKET)
    .createSignedUrl(thumbnailPath, expiresInSeconds)

  if (error || !data) {
    console.error('Error getting clip thumbnail URL:', error)
    return null
  }

  // Cache the URL (expires 1 hour before actual expiry for safety)
  urlCache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + (expiresInSeconds - 3600) * 1000
  })

  return data.signedUrl
}

export async function getVfxShotThumbnailUrl(thumbnailPath: string | null | undefined): Promise<string | null> {
  if (!thumbnailPath) return null

  // Check cache first
  const cacheKey = `vfx:${thumbnailPath}`
  const cached = urlCache.get(cacheKey)
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url
  }

  // Generate new signed URL (valid for 7 days)
  const expiresInSeconds = 7 * 24 * 60 * 60 // 7 days
  const { data, error } = await supabase.storage
    .from(VFX_SHOT_THUMBNAILS_BUCKET)
    .createSignedUrl(thumbnailPath, expiresInSeconds)

  if (error || !data) {
    console.error('Error getting VFX shot thumbnail URL:', error)
    return null
  }

  // Cache the URL (expires 1 hour before actual expiry for safety)
  urlCache.set(cacheKey, {
    url: data.signedUrl,
    expiresAt: Date.now() + (expiresInSeconds - 3600) * 1000
  })

  return data.signedUrl
}
